// API wrappers pour le Module Minisites
import axios from "axios";

import { getAuthHeaders } from "./auth";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/minisite`;
const BD_API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/bd`;

export async function listClients() {
  const r = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
  return r.data.clients;
}

export async function getMinisite(clientId) {
  const r = await axios.get(`${API}/${clientId}`, { headers: getAuthHeaders() });
  return r.data;
}

export async function upsertMinisite(clientId, draft) {
  const r = await axios.post(`${API}/${clientId}`, draft, {
    headers: getAuthHeaders(),
  });
  return r.data;
}

export async function deleteMinisite(clientId) {
  const r = await axios.delete(`${API}/${clientId}`, { headers: getAuthHeaders() });
  return r.data;
}

export async function uploadPhoto(clientId, file, slot) {
  const form = new FormData();
  form.append("file", file);
  form.append("slot", slot);
  const r = await axios.post(`${API}/${clientId}/photos`, form, {
    headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

export async function listBdPhotos(clientId) {
  const r = await axios.get(`${BD_API}/clients/${clientId}/photos`, {
    headers: getAuthHeaders(),
  });
  return r.data.photos || [];
}

export async function importPhotoFromBd(clientId, filename) {
  const r = await axios.post(
    `${API}/${clientId}/photos/import-from-bd`,
    { filename },
    { headers: getAuthHeaders() }
  );
  return r.data;
}

export function photoUrl(clientId, filename) {
  // Public endpoint — minisites are served on *.minibio.pro, photos go through
  // the same path without auth so rendered images work on the live site.
  return `${API}/${clientId}/photos/${filename}`;
}

export async function deletePhoto(clientId, filename) {
  const r = await axios.delete(`${API}/${clientId}/photos/${filename}`, {
    headers: getAuthHeaders(),
  });
  return r.data;
}

export async function extractFromAutobio(clientId, overwrite = false) {
  const r = await axios.post(
    `${API}/${clientId}/extract`,
    { overwrite },
    { headers: getAuthHeaders() }
  );
  return r.data;
}

export async function publishMinisite(clientId) {
  const r = await axios.post(`${API}/${clientId}/publish`, null, {
    headers: getAuthHeaders(),
  });
  return r.data;
}

export async function getPublishStatus(clientId) {
  const r = await axios.get(`${API}/${clientId}/publish/status`, {
    headers: getAuthHeaders(),
  });
  return r.data;
}

export async function listVersions(clientId) {
  const r = await axios.get(`${API}/${clientId}/versions`, {
    headers: getAuthHeaders(),
  });
  return r.data.versions;
}

export async function restoreVersion(clientId, versionId) {
  const r = await axios.post(
    `${API}/${clientId}/versions/${versionId}/restore`,
    null,
    { headers: getAuthHeaders() }
  );
  return r.data;
}

export async function chatHistory(clientId) {
  const r = await axios.get(`${API}/${clientId}/chat/history`, {
    headers: getAuthHeaders(),
  });
  return r.data.messages;
}

export async function chatTurn(clientId, message) {
  const r = await axios.post(
    `${API}/${clientId}/chat`,
    { message },
    { headers: getAuthHeaders() }
  );
  return r.data;
}

// Aperçu : le HTML est celui du site publié, les images sont servies à part,
// authentifiées (les planches BD ne sont publiques qu'une fois le site en ligne).
export async function fetchPreviewHtml(clientId) {
  const r = await axios.get(`${API}/${clientId}/preview-html`, {
    headers: getAuthHeaders(),
    responseType: "text",
  });
  return r.data;
}

// Image en `data:` URL, ou null quand elle n'est pas dans l'aperçu (chapitre
// non validé, planche absente) : c'est l'écran qui le signale.
//
// `data:` et non `blob:` : l'aperçu s'affiche dans une iframe cloisonnée, donc
// en origine opaque, où le navigateur refuse de charger un `blob:` créé par la
// page parente — toutes les images de l'aperçu apparaissaient cassées. Le
// serveur renvoie la version compressée, celle que le site publié sert.
export async function fetchPreviewAsset(clientId, name) {
  try {
    const r = await axios.get(
      `${API}/${clientId}/preview-assets/${encodeURIComponent(name)}`,
      { headers: getAuthHeaders(), responseType: "blob" }
    );
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(r.data);
    });
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}
